import FilterForm from '../../../components/forms/FilterForm';
import { Button, Form, Input, Modal, Space, Table } from 'antd';
import {
  CrudFilters,
  HttpError,
  useCan,
  useCreate,
  useNotification
} from '@refinedev/core';
import { DeleteButton, TextField, useTable } from '@refinedev/antd';
import { ISsoWhitelistEmail } from 'interfaces/sso_whitelist_emails';
import { ListLayout } from 'components/layout/layout/ListLayout';
import { formatDate } from 'services/date';
import { useState } from 'react';

interface IFilter {
  email: string;
}

export const SsoWhitelistEmailList = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm<{ email: string }>();
  const { open } = useNotification();
  const canCreate = useCan({
    action: 'create',
    resource: 'sso_whitelist_emails'
  });
  const { mutateAsync: createWhitelistEmail, isLoading: isCreating } =
    useCreate();
  const { tableProps, searchFormProps, tableQueryResult } = useTable<
    ISsoWhitelistEmail,
    HttpError,
    IFilter
  >({
    pagination: {
      mode: 'server'
    },
    onSearch: params => {
      const filters: CrudFilters = [];
      const { email } = params;

      filters.push({
        field: 'filter[by_email]',
        operator: 'eq',
        value: email
      });

      return filters;
    }
  });

  const handleCreate = async () => {
    try {
      await createForm.validateFields();
      await createWhitelistEmail({
        resource: 'sso_whitelist_emails',
        values: createForm.getFieldsValue()
      });

      open?.({
        type: 'success',
        message: 'E-mail adicionado à lista de permissão.'
      });

      createForm.resetFields();
      setIsCreateModalOpen(false);
      tableQueryResult.refetch();
    } catch (error) {
      if (error instanceof Error) {
        open?.({
          type: 'error',
          message: 'Não foi possível adicionar o e-mail.',
          description: error.message
        });
      }
    }
  };

  return (
    <ListLayout
      title="Lista de Permissão (SSO)"
      createButtonProps={{
        hidden: !canCreate?.data?.can,
        onClick: () => setIsCreateModalOpen(true)
      }}
    >
      <FilterForm searchFormProps={searchFormProps}>
        <Form.Item name="email">
          <Input placeholder="Buscar por e-mail" className="lg:min-w-[300px]" />
        </Form.Item>
      </FilterForm>

      <Table {...tableProps} rowKey="id">
        <Table.Column
          dataIndex="email"
          key="email"
          title="E-mail"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="created_at"
          key="created_at"
          title="Criado em"
          render={value => <TextField value={formatDate(value)} />}
        />
        <Table.Column<ISsoWhitelistEmail>
          title="Ações"
          dataIndex="actions"
          align="center"
          width={100}
          render={(_, record) => (
            <Space>
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>

      <Modal
        open={isCreateModalOpen}
        title="Adicionar e-mail"
        onCancel={() => setIsCreateModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsCreateModalOpen(false)}>
            Cancelar
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={isCreating}
            onClick={handleCreate}
          >
            Adicionar
          </Button>
        ]}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            label="E-mail"
            name="email"
            rules={[
              { required: true, message: 'Informe um e-mail.' },
              { type: 'email', message: 'Informe um e-mail válido.' }
            ]}
          >
            <Input placeholder="nome@dominio.com" />
          </Form.Item>
        </Form>
      </Modal>
    </ListLayout>
  );
};
